import { ContentChefClient } from "@contentchef/contentchef-management-js-client";
import { ContentId, ContentStatus } from "@contentchef/contentchef-types";
import { action, observable, runInAction } from "mobx";

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const POLL_INTERVAL_MS = 1500;
// A publish lands in seconds; past this one has failed, or never changed the status, and the page
// shows what it has until the next load.
const POLL_DEADLINE_MS = 60_000;

class ContentStatusStore {
  @observable status: ContentStatus[] | null = null;
  @observable defaultStatus: ContentStatus | null = null;
  // Bumped by every poll and by stopPolling: a loop that finds it changed has been replaced.
  private pollRun = 0;
  constructor(readonly api: ContentChefClient) {}

  @action.bound
  async getContentStatus(id: ContentId) {
    try {
      const result = await this.api.contents.getContentStatus({ id });
      return result;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  @action.bound
  async setContentStatus(id: ContentId) {
    try {
      const status = await this.getContentStatus(id);
      runInAction(() => {
        this.defaultStatus = this.getOriginalStatus(status);
        this.status = status;
      });
    } catch (error) {
      console.error(error);
    }
  }

  /** After a publish to stage, until its request shows in the status: a new `stagePublishingId`
   *  (every publish gets one), not a new `stageVersion`, which a content nobody edited keeps. */
  @action.bound
  async pollUntilStatusChanges(id: ContentId) {
    const run = ++this.pollRun;
    const before = this.defaultStatus?.stagePublishingId;
    const deadline = Date.now() + POLL_DEADLINE_MS;
    let status = await this.getContentStatus(id);
    let originalStatus = this.getOriginalStatus(status);

    while (!originalStatus || originalStatus.stagePublishingId === before) {
      if (run !== this.pollRun || Date.now() >= deadline) return;
      await delay(POLL_INTERVAL_MS);
      if (run !== this.pollRun) return;
      status = await this.getContentStatus(id);
      originalStatus = this.getOriginalStatus(status);
    }
    if (run !== this.pollRun) return;
    runInAction(() => {
      this.defaultStatus = originalStatus;
      this.status = status;
    });
  }

  /** The edit page is going: a poll still running ends at its next step. */
  stopPolling() {
    this.pollRun++;
  }

  private getOriginalStatus(status: ContentStatus[]) {
    return status.find((item) => item.isTranslation === false || item.isTranslation === undefined)!;
  }
}

export default ContentStatusStore;
